import { useTranslation } from 'react-i18next'

import { getLandingPageUrl, isCustomUrl } from '@pages/IntroducingBroker/LinkHub/Links/helpers'
import { useLinkHub } from '@pages/IntroducingBroker/LinkHub/Links/hooks/useLinkHub'

import { useGAEvents } from '@utils/callGAEvents'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import { SectionHeader } from '../components/SectionHeader/SectionHeader'
import { ReferralCodeField } from './ReferralCodeField'
import { ReferralLandingPageSelect } from './ReferralLandingPageSelect'
import { ReferralLinkUrlField } from './ReferralLinkUrlField'

import styles from './ReferralLink.module.scss'

interface IBReferralLinkProps {
  ibCode?: string
  mtCode?: string
  ibLink?: string
  mtLink?: string
  isLoading: boolean
  fetchShortenedIBLink: (
    referralCode: string,
    shortenUrl?: boolean
  ) => Promise<{ shortUrl: string; url: string }>
}

export interface CopyDataList {
  name: string
  value: string
  onCopyGAEventClick?: () => void
  isReferralLink?: boolean
  referralCode?: string
  referralType?: number
}

export const CUSTOM_LANDING_PAGE_OPTION_ID = '0'

export const ReferralLink = (props: IBReferralLinkProps) => {
  const { ibCode, mtCode, ibLink, mtLink, isLoading, fetchShortenedIBLink } = props
  const { callGAEvents } = useGAEvents()
  const { t } = useTranslation()

  const {
    ibCodes,
    links,
    shortening,
    landingPages: landingPagesData,
  } = useLinkHub({ ibCode, mtCode, ibLink, mtLink, fetchShortenedIBLink, isLinkHub: false })

  const { mainReferralLink } = links
  const {
    shortenedMainReferralLink,
    setShortenedMainReferralLink,
    shortenedMTReferralLink,
    isShortMainLinkLoading,
    isShortMTLinkLoading,
    isShorteningAvailable,
    handleIBLinkShortening,
  } = shortening
  const {
    landingPageUrl,
    setLandingPageUrl,
    customLandingPageUrl,
    setCustomLandingPageUrl,
    landingPages,
    isLoadingLpUrlsFromCategories,
    isCustomLpUrlSelected,
  } = landingPagesData

  return (
    <div className={styles.iBReferralLink}>
      <SectionHeader
        title={t('IB.Dashboard.Referral Link')}
        seeMoreLinkProps={{
          onClick: () => callGAEvents().IBDashboard.ReferralLink.seeMore(),
          to: '/dashboard/introducing-broker/referral-materials',
        }}
      />
      <Loading isLoading={isLoading} showLoadingIcon>
        <Box flex direction='column' gap='x16' mt='x16'>
          {ibCode && (
            <ReferralLandingPageSelect
              ibCode={ibCode}
              landingPageUrl={landingPageUrl}
              setLandingPageUrl={setLandingPageUrl}
              shortenedReferralLink={shortenedMainReferralLink}
              setShortenedReferralLink={setShortenedMainReferralLink}
              customTargetUrl={customLandingPageUrl}
              setCustomLandingPageUrl={setCustomLandingPageUrl}
              landingPageUrlsFromCategories={landingPages}
              isLoadingLpUrlsFromCategories={isLoadingLpUrlsFromCategories}
              isShortLinkLoading={isShortMainLinkLoading || isShortMTLinkLoading}
            />
          )}

          {ibCode && (mainReferralLink || shortenedMainReferralLink) && (
            <ReferralLinkUrlField
              buttonVariant='ghost'
              referralLink={mainReferralLink}
              ibCode={ibCode}
              shortenedReferralLink={shortenedMainReferralLink}
              isShortLinkLoading={isShortMainLinkLoading}
              onShortenIBLink={handleIBLinkShortening}
              isShorteningAvailable={isShorteningAvailable.canShortenMainIB}
              isCustomUrl={isCustomUrl({
                isCustomLpUrlSelected,
                isLinkShortened: !!shortenedMainReferralLink,
                isLinkExist: !!mainReferralLink,
                id: landingPageUrl?.id,
              })}
              landingPageUrl={getLandingPageUrl({
                isCustomLpUrlSelected,
                customLandingPageUrl,
                id: landingPageUrl?.id,
              })}
            />
          )}
          {mtCode && !ibCode && (mtLink || shortenedMainReferralLink) && (
            <ReferralLinkUrlField
              buttonVariant='ghost'
              label={`${t('IB.Dashboard.Referral Link for registration')} (${t(
                'Subscriptions.Multi-Tier'
              )})`}
              referralLink={mtLink}
              ibCode={mtCode}
              shortenedReferralLink={shortenedMTReferralLink}
              isShortLinkLoading={isShortMTLinkLoading}
              onShortenIBLink={handleIBLinkShortening}
              isShorteningAvailable={isShorteningAvailable.canShortenMTIB}
              isCustomUrl
            />
          )}
        </Box>
        <ul className={styles.iBReferralLinkList}>
          {ibCodes.map((item) => {
            return <ReferralCodeField key={item.value} item={item} value={item.value} />
          })}
        </ul>
      </Loading>
    </div>
  )
}
